import React, { useState, useEffect } from "react";
import DefaultInput from "../../inputs/defaultInput";
import DefaultSearchSelectInput from "../../inputs/defaultSearchSelectInput";
import DefaultButton from "../../buttons/defaultButton";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import { HiUserGroup } from "react-icons/hi";
import toast from "react-hot-toast";
import { getStaffList } from "../../../../services/workspace/staff.service";
import { convertToLabelValue } from "../../../../utils/common";
import { getUserList } from "../../../../services/workspace/user.service";

const TeamsStep = ({ value = [], onChange, departments = [] }) => {
  const [teams, setTeams] = useState(value);
  const [newTeam, setNewTeam] = useState({
    name: "",
    departmentId: null,
    leadId: null,
  });
  const [leadOptions, setLeadOptions] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Fetch staff list for team leads (type 2 = staff only)
  useEffect(() => {
    const fetchLeads = async () => {
      setLoadingLeads(true);
      try {
        const response = await getUserList({ type: 2, additional_info: true });
        const staffData = response?.user?.data || response?.data || response || [];
        
        // Use false (or omit) for needUser since user objects have 'id' not 'user_id'
        const formattedLeads = convertToLabelValue(staffData, false);
        
        setLeadOptions(formattedLeads || []);
      } catch (error) {
        console.error("Failed to fetch staff list for team leads:", error);
        toast.error("Failed to load team leads");
      } finally {
        setLoadingLeads(false);
      }
    };

    fetchLeads();
  }, []);

  const handleAddTeam = () => {
    if (!newTeam.name.trim() || !newTeam.departmentId) {
      if (!newTeam.name.trim()) {
        toast.error("Team name is required");
      } else if (!newTeam.departmentId) {
        toast.error("Department is required");
      }
      return;
    }

    const team = {
      id: `team-${Date.now()}`,
      name: newTeam.name.trim(),
      departmentId: newTeam.departmentId,
      leadId: newTeam.leadId,
    };

    const updated = [...teams, team];
    setTeams(updated);
    onChange(updated);
    setNewTeam({ name: "", departmentId: null, leadId: null });
  };

  const handleRemoveTeam = (id) => {
    const updated = teams.filter((t) => t.id !== id);
    setTeams(updated);
    onChange(updated);
  };

  const handleUpdateTeam = (id, field, value) => {
    const updated = teams.map((t) =>
      t.id === id ? { ...t, [field]: value } : t
    );
    setTeams(updated);
    onChange(updated);
  };

  const getDepartmentOptions = () => {
    return departments.map((d) => ({ value: d.id, label: d.name }));
  };

  // Get lead options from fetched staff list
  const getLeadOptions = () => {
    return leadOptions;
  };

  const getTeamDepartment = (departmentId) => {
    return departments.find((d) => d.id === departmentId)?.name || "N/A";
  };

  return (
    <div className="flex flex-col gap-6" role="region" aria-labelledby="teams-heading">
      <div>
        <h2 id="teams-heading" className="text-xl font-semibold text-theme mb-2">
          Teams Setup
        </h2>
        <p className="text-sm text-textLight">
          Create teams and assign them to departments. Team leads are optional.
        </p>
      </div>

      {/* Add Team Form */}
      <div className=" rounded-lg p-4 border border-borderColor">
        <h3 className="text-sm font-semibold text-theme mb-3">Add Team</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <DefaultInput
            label="Team Name"
            placeholder="e.g., Frontend Team, Sales Team"
            value={newTeam.name}
            onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
            required
          />
          <DefaultSearchSelectInput
            label="Department"
            value={
              newTeam.departmentId
                ? getDepartmentOptions().find(
                    (o) => o.value === newTeam.departmentId
                  )
                : null
            }
            onChange={(selected) =>
              setNewTeam({
                ...newTeam,
                departmentId: selected?.value || null,
              })
            }
            options={getDepartmentOptions()}
            required
            placeholder="Select department"
          />
          <DefaultSearchSelectInput
            label="Team Lead (Optional)"
            value={
              newTeam.leadId
                ? getLeadOptions().find((o) => o.value === newTeam.leadId)
                : null
            }
            onChange={(selected) =>
              setNewTeam({ ...newTeam, leadId: selected?.value || null })
            }
            options={getLeadOptions()}
            // disabled={loadingLeads}
            placeholder={loadingLeads ? "Loading team leads..." : "Select team lead (optional)"}
          />
          <div className="flex items-end justify-end">
            <DefaultButton
              variant="default"
              name="Add Team"
              icon={<IoMdAdd />}
              onClick={handleAddTeam}
              disabled={!newTeam.name.trim() || !newTeam.departmentId}
            />
          </div>
        </div>
      </div>

      {/* Teams List */}
      {teams.length > 0 && (
        <div className="flex flex-col gap-4">
          <h3 className="text-sm font-semibold text-theme">
            Teams ({teams.length})
          </h3>
          <div className="space-y-2">
            {teams.map((team) => (
              <div
                key={team.id}
                className="grid grid-cols-4 items-center gap-3 p-3 bg-white border border-borderColor rounded-md"
              >
                <div className="col-span-1">
                  <DefaultInput
                    value={team.name}
                    onChange={(e) =>
                      handleUpdateTeam(team.id, "name", e.target.value)
                    }
                    classnames="mb-0"
                  />
                </div>
                <div className="col-span-1">
                  <DefaultSearchSelectInput
                  value={
                    team.departmentId
                      ? getDepartmentOptions().find(
                          (o) => o.value === team.departmentId
                        )
                      : null
                  }
                  onChange={(selected) =>
                    handleUpdateTeam(
                      team.id,
                      "departmentId",
                      selected?.value || null
                    )
                  }
                  options={getDepartmentOptions()}
                  placeholder="Department"
                  lebelVertical={false}
                  />
                </div>
                <div className="col-span-1 w-full">
                  <DefaultSearchSelectInput
                    value={
                      team.leadId
                        ? getLeadOptions().find((o) => o.value === team.leadId)
                        : null
                    }
                    onChange={(selected) =>
                      handleUpdateTeam(team.id, "leadId", selected?.value || null)
                    }
                    options={getLeadOptions()}
                    placeholder="Team Lead"
                    lebelVertical={false}
                    customWidth="!w-full block"
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  <DefaultButton
                    variant="danger"
                    icon={<IoMdClose />}
                    size="icon"
                    onClick={() => handleRemoveTeam(team.id)}
                    aria-label={`Remove ${team.name}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Teams Summary */}
      {teams.length > 0 && (
        <div className=" rounded-lg p-4 border border-borderColor">
          <h3 className="text-sm font-semibold text-theme mb-3">
            Teams by Department
          </h3>
          <div className="space-y-2">
            {departments.map((dept) => {
              const deptTeams = teams.filter((t) => t.departmentId === dept.id);
              if (deptTeams.length === 0) return null;
              return (
                <div key={dept.id} className="bg-white rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <HiUserGroup className="text-theme" />
                    <span className="text-sm font-medium text-theme">
                      {dept.name}
                    </span>
                    <span className="text-xs text-textLight">
                      ({deptTeams.length} team{deptTeams.length !== 1 ? "s" : ""})
                    </span>
                  </div>
                  <div className="ml-6 space-y-1">
                    {deptTeams.map((team) => (
                      <div
                        key={team.id}
                        className="text-sm text-theme flex items-center gap-2"
                      >
                        <span className="w-1.5 h-1.5 bg-theme rounded-full"></span>
                        {team.name}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {teams.length === 0 && (
        <div className="text-center py-8 text-textLight">
          <p>No teams created yet. Add your first team above.</p>
        </div>
      )}
    </div>
  );
};

export default TeamsStep;

